<!-- Copyright 2019, Nathan Lecompte -->

<template>
	<div id="app" :class="{ md: (loaded || settings.visible) }" v-cloak>
		<!-- Navigation menu -->
		<header :class="[{ static: !settings.config.stickyNav }, { mds: settings.visible }]">
			<!-- Searchbox -->
			<div class="search">
				<div class="lt">
					<!-- Logo/Back-button -->
					<svg 
						:class="['logo-back', {'hide-logo-back': (!loaded && !settings.visible) }]" 
						@keydown.enter="logoClick()" 
						@click="logoClick()" 
						tabindex="0" 
						viewBox="0 0 512 512" 
						xmlns="http://www.w3.org/2000/svg">
						<path d="m217.9 256 127.1-127c9.4-9.4 9.4-24.6 0-33.9-9.4-9.4-24.6-9.3-34 0l-144 143.9c-9.1 9.1-9.3 23.7-.7 33.1l144.6 144.9c4.7 4.7 10.9 7 17 7s12.3-2.3 17-7c9.4-9.4 9.4-24.6 0-33.9z"/>
					</svg>
					<svg 
						:class="['logo', {'hide-logo': (loaded || settings.visible) }]" 
						viewBox="0 0 1000 1000" 
						xmlns="http://www.w3.org/2000/svg">
						<path d="m500 5c-273.38 0-495 221.62-495 495s221.62 495 495 495 495-221.62 495-495-221.62-495-495-495zm0 737.55c-106.45 0-193-78.35-193-174.66 0-14.6 5.36-42.17 30.91-90.65 17.2-32.65 41.3-70.62 71.62-112.85 35.36-49.25 71.08-92.92 90.52-116 19.44 23.12 55.16 66.79 90.52 116 30.32 42.23 54.42 80.2 71.62 112.85 25.55 48.48 30.91 76 30.91 90.65-.05 96.31-86.65 174.66-193.1 174.66z"/>
					</svg>
				
					<!-- Settings title-->
					<h1 v-if="settings.visible" class="h1-settings">Settings</h1>
				</div>

				<span>
					<!-- Search query input -->
					<label for="q" v-if="!settings.visible">Enter your search query:</label>
					<input 
						v-if="!settings.visible" 
						v-model="query" 
						@focus="paramQuery=false"
						@keypress="autocompleter.selecting=false"
						@keyup.enter="search(query, nav.active)"
						@keydown.up.prevent="autoCompleteUp()" 
						@keydown.down.prevent="autoCompleteDown()"
						id="q" 
						name="q" 
						type="text" 
						value="" 
						title="Search" 
						placeholder="Search the web..." 
						aria-label="Enter your search query" 
						tabindex="0" 
						autocapitalize="off" 
						autocomplete="off" 
						autocorrect="off" 
						spellcheck="false" 
						autofocus 
						enterkeyhint="search" 
						mozactionhint="search"/>
					
					<!-- Autocomplete menu -->
					<transition name="woosh">
						<ul class="ac" v-if="query && autocompleter.visible && !settings.visible">
							<li 
								v-for="item in autocompleter.items"
								:key="item.id" 
								:class="{ active: autocompleter.active === item.id }" 
								@click="search(item.val, nav.active)"
								tabindex="0">
								{{ item.val }}
							</li>
						</ul>
					</transition>

					<!-- Search button -->
					<button 
						v-if="!settings.visible" 
						@click="search(query, nav.active)" 
						id="qb" 
						title="Search" 
						aria-label="Submit search query"
						tabindex="0" 
						enterkeyhint="search" 
						mozactionhint="search">
						<svg viewBox="0 0 1792 1792" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
							<path d="m1216 832q0-185-131.5-316.5t-316.5-131.5-316.5 131.5-131.5 316.5 131.5 316.5 316.5 131.5 316.5-131.5 131.5-316.5zm512 832q0 52-38 90t-90 38q-54 0-90-38l-343-342q-179 124-399 124-143 0-273.5-55.5t-225-150-150-225-55.5-273.5 55.5-273.5 150-225 225-150 273.5-55.5 273.5 55.5 225 150 150 225 55.5 273.5q0 220-124 399l343 343q37 37 37 90z"/>
						</svg>
					</button>

					<!-- Divider for settings title -->
					<span v-if="settings.visible" class="divider"></span>

					<!-- Overflow menu button -->
					<button 
						v-if="(nav.items.length > 1) && nav.overflow && settings.config.categories.length != 1"
						@click="nav.visible = !nav.visible" 
						class="overflow" 
						title="Menu" 
						aria-haspopup="true" 
						:aria-expanded="(nav.items.length > 1) && (!nav.overflow || nav.visible)" 
						tabindex="0">
						<svg v-if="!nav.visible" viewBox="0 0 96 96" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
							<path d="m78 27h-60c-3.3 0-6 2.7-6 6s2.7 6 6 6h60c3.3 0 6-2.7 6-6s-2.7-6-6-6z"/>
							<path d="m78 57h-60c-3.3 0-6 2.7-6 6s2.7 6 6 6h60c3.3 0 6-2.7 6-6s-2.7-6-6-6z"/>
						</svg>
						<span v-else>
							<svg viewBox="0 0 512 512" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
								<path d="m437.5 386.6-130.6-130.6 130.6-130.6c14.1-14.1 14.1-36.8 0-50.9s-36.8-14.1-50.9 0l-130.6 130.6-130.6-130.6c-14.1-14.1-36.8-14.1-50.9 0s-14.1 36.8 0 50.9l130.6 130.6-130.6 130.6c-14.1 14.1-14.1 36.8 0 50.9s36.8 14.1 50.9 0l130.6-130.6 130.6 130.6c14.1 14.1 36.8 14.1 50.9 0 14-14.1 14-36.9 0-50.9z"/>
							</svg>
						</span>
					</button>
				</span>
			</div>

			<!-- Categories / Settings menu -->
			<transition name="woosh">
				<nav 
					v-if="(nav.items.length > 1) && (!nav.overflow || nav.visible)" 
					:class="{ 'o-nav': nav.overflow }" 
					:hidden="!((nav.items.length > 1) && (!nav.overflow || nav.visible))" 
					role="menu">
					<!-- Overflow menu close button -->
					<a 
						v-if="!loaded && nav.overflow && !settings.visible" 
						@keydown.enter="nav.visible = !nav.visible" 
						@click="nav.visible = !nav.visible" 
						class="o-close" 
						role="menuitem" 
						tabindex="0">
						<svg viewBox="0 0 512 512" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
							<path d="m437.5 386.6-130.6-130.6 130.6-130.6c14.1-14.1 14.1-36.8 0-50.9s-36.8-14.1-50.9 0l-130.6 130.6-130.6-130.6c-14.1-14.1-36.8-14.1-50.9 0s-14.1 36.8 0 50.9l130.6 130.6-130.6 130.6c-14.1 14.1-14.1 36.8 0 50.9s36.8 14.1 50.9 0l130.6-130.6 130.6 130.6c14.1 14.1 36.8 14.1 50.9 0 14-14.1 14-36.9 0-50.9z"/>
						</svg>
					</a>

					<!-- Category menu buttons -->
					<span v-for="item in nav.items" :key="item.id">
						<a 
							v-if="settings.config.categories.indexOf(item.id) > -1 && settings.config.categories.length != 1 && !settings.visible" 
							:class="{ active: item.id === nav.active.id }"
							@click="navClick(item)" 
							:title="item.content" 
							:aria-label="item.content" 
							role="menuitem" 
							tabindex="0" 
							href="#">
							{{ item.content }}
						</a>
					</span>

					<!-- Settings menu buttons -->
					<span v-for="item in settings.nav.items" :key="item">
						<a 
							v-if="settings.visible" 
							:class="{ active: item === settings.nav.active }"
							@click="settings.nav.active = item; nav.visible = false" 
							:title="item" 
							:aria-label="item" 
							role="menuitem" 
							tabindex="0" 
							href="#">
							{{ item }}
						</a>
					</span>

					<!-- Settings button -->
					<a 
						v-if="settings.config.categories.length != 1 && !settings.visible" 
						:class="['nav-settings', { active: settings.visible }]" 
						@click="settingsClick()" 
						title="Settings" 
						aria-label="Settings" 
						role="menuitem" 
						tabindex="0" 
						href="#">
						<svg viewBox="0 0 96 96" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
							<path d="m78 27h-42c-3.3 0-6 2.7-6 6s2.7 6 6 6h42c3.3 0 6-2.7 6-6s-2.7-6-6-6z"/>
							<circle cx="16" cy="33" r="8"/>
							<path d="m18 69h42c3.3 0 6-2.7 6-6s-2.7-6-6-6h-42c-3.3 0-6 2.7-6 6s2.7 6 6 6z"/>
							<path d="m88 63c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8"/>
						</svg>
					</a>
				</nav>
			</transition>

			<!-- Settings button -->
			<a 
				v-if="(nav.items.length > 1) && !settings.visible && settings.config.categories.length === 1" 
				:class="['nav-settings', { active: settings.visible }]" 
				@click="settingsClick()" 
				title="Settings" 
				aria-label="Settings" 
				tabindex="0" 
				href="#">
				<svg viewBox="0 0 96 96" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
					<path d="m78 27h-42c-3.3 0-6 2.7-6 6s2.7 6 6 6h42c3.3 0 6-2.7 6-6s-2.7-6-6-6z"/>
					<circle cx="16" cy="33" r="8"/>
					<path d="m18 69h42c3.3 0 6-2.7 6-6s-2.7-6-6-6h-42c-3.3 0-6 2.7-6 6s2.7 6 6 6z"/>
					<path d="m88 63c0 4.4-3.6 8-8 8s-8-3.6-8-8 3.6-8 8-8 8 3.6 8 8"/>
				</svg>
			</a>
		</header>

		<!-- Information bubble -->
		<transition name="info" mode="out-in">
			<section v-if="info" class="info" aria-label="Information bubble">
				<button @click="info = ''" title="Close" aria-label="Close information bubble" tabindex="0">
					<svg viewBox="0 0 512 512" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
						<path d="m437.5 386.6-130.6-130.6 130.6-130.6c14.1-14.1 14.1-36.8 0-50.9s-36.8-14.1-50.9 0l-130.6 130.6-130.6-130.6c-14.1-14.1-36.8-14.1-50.9 0s-14.1 36.8 0 50.9l130.6 130.6-130.6 130.6c-14.1 14.1-14.1 36.8 0 50.9s36.8 14.1 50.9 0l130.6-130.6 130.6 130.6c14.1 14.1 36.8 14.1 50.9 0 14-14.1 14-36.9 0-50.9z"/>
					</svg>
				</button>
				<span>{{ info }}</span>
			</section>
		</transition>

		<!-- Loading indicator -->
		<transition name="woosh" mode="out-in">
			<div v-show="loaded && loading && page === 1 && !settings.visible" class="loading">
				<svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
					<path d="m500 5c-273.38 0-495 221.62-495 495s221.62 495 495 495 495-221.62 495-495-221.62-495-495-495zm0 737.55c-106.45 0-193-78.35-193-174.66 0-14.6 5.36-42.17 30.91-90.65 17.2-32.65 41.3-70.62 71.62-112.85 35.36-49.25 71.08-92.92 90.52-116 19.44 23.12 55.16 66.79 90.52 116 30.32 42.23 54.42 80.2 71.62 112.85 25.55 48.48 30.91 76 30.91 90.65-.05 96.31-86.65 174.66-193.1 174.66z"/>
				</svg>
			</div>
		</transition>

		<!-- Search results / Settings -->
		<transition name="woosh" mode="out-in">
			<!-- Search results -->
			<section v-if="loaded && !searching && !settings.visible" class="results" key="1">
				<!-- Smart search results -->
				<section v-if="res.length != 0 && sres.length != 0 && nav.active.smart && settings.config.smartResults" :class="nav.active.smart.name">
					<component 
						v-for="item in sres"
						:is="nav.active.smart.component"
						:item="item"
						:key="item.id">
					</component>
				</section>

				<!-- Regular search results -->
				<section v-if="res.length != 0" :class="nav.active.name">
					<section v-if="sgres.length != 0 && settings.config.suggestions" class="res-suggestions">
						<span v-for="item in sgres" :key="item.id">
							<a 
								@click="search(item.title, nav.active)" 
								:title="item.title" 
								:aria-label="item.title" 
								tabindex="0" 
								href="#">
								{{ item.title }}
							</a>
						</span>
					</section>
					<component 
						v-for="item in res"
						:is="nav.active.component"
						:item="item"
						:key="item.id"
						tabindex="0">
					</component>
					<button 
						v-if="!pageEnd && !loading && !settings.config.autoload" 
						@click="loadClick()" 
						class="res-load" 
						title="Load more results" 
						aria-label="Load more results"
						tabindex="0">
						<svg viewBox="0 0 16 16" width="20" height="20" xmlns="http://www.w3.org/2000/svg">
							<circle cx="2" cy="8" r="1.6"/>
							<circle cx="8" cy="8" r="1.6"/>
							<circle cx="14" cy="8" r="1.6"/>
						</svg>
					</button>
					<div v-if="pageEnd || loaded && loading && page > 1 && !settings.visible" class="res-end">
						<p v-if="pageEnd">End of results</p>
						<div v-if="loaded && loading && page > 1 && !settings.visible" class="loading-small">
							<svg viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg">
								<path d="m500 5c-273.38 0-495 221.62-495 495s221.62 495 495 495 495-221.62 495-495-221.62-495-495-495zm0 737.55c-106.45 0-193-78.35-193-174.66 0-14.6 5.36-42.17 30.91-90.65 17.2-32.65 41.3-70.62 71.62-112.85 35.36-49.25 71.08-92.92 90.52-116 19.44 23.12 55.16 66.79 90.52 116 30.32 42.23 54.42 80.2 71.62 112.85 25.55 48.48 30.91 76 30.91 90.65-.05 96.31-86.65 174.66-193.1 174.66z"/>
							</svg>
						</div>
					</div>
				</section>
			</section>

			<!-- Settings -->
			<section v-else-if="settings.visible" class="settings" key="2">
				<transition name="woosh" mode="out-in">
					<!-- Settings > General -->
					<section v-if="settings.nav.active === 'General'" key="General">
						<!-- Categories setting -->
						<section>
							<h2>Categories</h2>
							<p>Select the preferred categories to enable and display in the navigation menu.</p>
							<list 
								:type="'checkbox'" 
								:title="'List of categories to enable and display'"
								:items="nav.items"
								:active="settings.config.categories"
								:minActiveItems="1"
								:onItemClick="toggleCategory"
								:enableDelete="false"
								:enableNew="false">
							</list>
						</section>

						<!-- Auto-load setting -->
						<section>
							<h2>Auto-load</h2>
							<p>Toggle whether to enable auto-load; this will automatically load more results before reaching the bottom of the page when scrolling (this may not be suitable for slow networks).
								<toggle :id="'autoloadToggle'" v-model="settings.config.autoload" :title="'Toggle auto-load function'" :onToggle="resetSearch"></toggle>
							</p>
						</section>

						<!-- Safe-search setting -->
						<section>
							<h2>Safe-search</h2>
							<p>Toggle whether to enable safe-search; this will attempt to filter-out adult content (functionality depends on the selected Searx instance).
								<toggle :id="'safeSearchToggle'" v-model="settings.config.safeSearch" :title="'Toggle safe-search function'" :onToggle="resetSearch"></toggle>
							</p>
						</section>

						<!-- Search parameters setting -->
						<section>
							<h2>Search parameters</h2>
							<p>Toggle whether to enable search parameters in the URL; this means searches will be visible in your browser history and allows searches to be bookmarked.
								<toggle :id="'searchParamsToggle'" v-model="settings.config.searchParams" :title="'Toggle search parameters function'" :onToggle="resetSearch"></toggle>
							</p>
						</section>

						<!-- Smart results setting -->
						<section>
							<h2>Smart results</h2>
							<p>Toggle whether to enable smart results (such as Wikipedia snippets for the General category); this functionality depends on the selected Searx instance.
								<toggle :id="'smartResultsToggle'" v-model="settings.config.smartResults" :title="'Toggle smart results function'" :onToggle="resetSearch"></toggle>
							</p>
						</section>

						<!-- Suggestions setting -->
						<section>
							<h2>Suggestions</h2>
							<p>Toggle whether to enable suggestions; this will suggest similar queries in the search results.
								<toggle :id="'suggestionsToggle'" v-model="settings.config.suggestions" :title="'Toggle suggestions function'" :onToggle="resetSearch"></toggle>
							</p>
						</section>

						<hr>

						<!-- Add-to-Home-screen setting -->
						<section>
							<h2>Add to Home screen</h2>
							<p>Add Moondrop Search to your Home screen; additionally, since Moondrop Search follows the OpenSearch description format, most browsers should automatically detect Moondrop Search as a search provider on first load (check your settings to set Moondrop Search as your default search provider).
								<button class="button" @click="addPWAClick()" tabindex="0">Add to Home screen</button>
							</p>
						</section>
					</section>

					<!-- Settings > Provider -->
					<section v-if="settings.nav.active === 'Provider'" key="Provider">
						<!-- Searx instances setting -->
						<section>
							<h2>Searx instances</h2>
							<p>Select the Searx instance to contact when searching; you can either select an instance from the list below or add your own instance.</p>
							<list 
								:type="'radio'" 
								:title="'List of Searx instances to select the current instance from'" 
								:items="settings.instances.items"
								:active="settings.instances.active"
								:onItemClick="setInstance"
								:enableDelete="true"
								:onDeleteClick="deleteInstance"
								:titleDelete="'Delete instance'"
								:enableNew="true"
								:onNewClick="addInstance"
								:placeholderNew="'Add new instance...'"
								:titleNew="'Add new instance'"
								:titleNewInput="'URL of instance'"
								:titleNewButton="'Add instance'">
							</list>
						</section>

						<!-- Scatter Select setting -->
						<section v-if="settings.instances.items.length>1">
							<h2>Scatter-select</h2>
							<p>Toggle whether to enable Scatter-select; this will randomly select a Searx instance from the list above for every new search.
								<toggle :id="'scatterSelectToggle'" v-model="settings.config.scatterSelect" :title="'Toggle Scatter-select function'" :onToggle="resetSearch"></toggle>
							</p>
						</section>
					</section>

					<!-- Settings > Appearance -->
					<section v-if="settings.nav.active==='Appearance'" key="Appearance">
						<!-- Style setting -->
						<section>
							<h2>Style</h2>
							<p>Select which style to enable and display; styles may modify the background color, text color, text opacity, link-visited color, accent color and font.</p>
							<list 
								:type="'radio'" 
								:title="'List of styles to select the current style from'"
								:items="settings.styles.items"
								:active="settings.styles.active"
								:onItemClick="setStyle"
								:enableDelete="false"
								:enableNew="false">
							</list>
						</section>
						
						<!-- Lite mode setting -->
						<section>
							<h2>Lite mode</h2>
							<p>Toggle whether to enable Lite mode; this disables all animations and stylised fonts (suitable for slow devices).
								<toggle :id="'liteModeToggle'" v-model="settings.config.liteMode" :title="'Toggle Lite mode function'" :onToggle="toggleLiteMode"></toggle>
							</p>
						</section>

						<!-- Overflow menu setting -->
						<section v-if="!isMobile()">
							<h2>Overflow menu</h2>
							<p>Toggle whether to always show the overflow menu.
								<toggle :id="'overflowToggle'" v-model="settings.config.overflowAlways" :title="'Toggle overflow menu function'" :onToggle="loadOverflow"></toggle>
							</p>
						</section>

						<!-- Sticky navigation menu setting -->
						<section>
							<h2>Sticky navigation</h2>
							<p>Toggle whether to stick the navigation menu to the top of the page when scrolling.
								<toggle :id="'stickyNavToggle'" v-model="settings.config.stickyNav" :title="'Toggle sticky navigation menu function'" :onToggle="onScroll"></toggle>
							</p>
						</section>
					</section>
					
					<!-- Settings > About -->
					<section v-if="settings.nav.active === 'About'" class="settings-about" key="About">
						<h1>
							<svg viewBox="0 0 1000 1000" width="40" height="40" xmlns="http://www.w3.org/2000/svg">
								<path d="m500 5c-273.38 0-495 221.62-495 495s221.62 495 495 495 495-221.62 495-495-221.62-495-495-495zm0 737.55c-106.45 0-193-78.35-193-174.66 0-14.6 5.36-42.17 30.91-90.65 17.2-32.65 41.3-70.62 71.62-112.85 35.36-49.25 71.08-92.92 90.52-116 19.44 23.12 55.16 66.79 90.52 116 30.32 42.23 54.42 80.2 71.62 112.85 25.55 48.48 30.91 76 30.91 90.65-.05 96.31-86.65 174.66-193.1 174.66z"/>
							</svg>
							Moondrop Search
						</h1>
						<p>Moondrop Search is a decentralised search engine powered by Searx.
							<ul>
								<li><b>Version</b>{{ settings.about.version }}</li>
								<li><b>License</b>{{ settings.about.license }}</li>
								<li><b>Source code</b><a :href="settings.about.source" tabindex="0">GitHub</a></li>
								<li><b>Credits</b>
									<ul>
										<li v-for="item in settings.about.credits" :key="item.id">
											<a :href="item.url" :title="item.title" :aria-label="item.title" tabindex="0">
												{{ item.title }} 
												<span v-if="item.license">
													({{ item.license }})
												</span>
											</a>
											&nbsp;&nbsp;{{ item.desc }}
										</li>
									</ul>
								</li>
							</ul>
						</p>
					</section>
				</transition>
			</section>
		</transition>
	</div>
</template>


<script>
import General from './components/categories/General.vue';
import S_General from './components/categories/S_General.vue';
import Images from './components/categories/Images.vue';
import Videos from './components/categories/Videos.vue';
import News from './components/categories/News.vue';

import Config from './config/Config.json';
import Instances from './config/Instances.json';
import Styles from './config/Styles.json';

import Toggle from './components/Toggle.vue';
import List from './components/List.vue';

const Categories = [
	{ id: 0, name: 'res', content: 'General', component: General, smart: { name:'s-res', component: S_General } },
	{ id: 1, name: 'i-res', content: 'Images', component: Images },
	{ id: 2, name: 'v-res', content: 'Videos', component: Videos },
	{ id: 3, name: 'n-res', content: 'News', component: News }
];

export default {
	name: 'app',
	components: {
		'toggle': Toggle,
		'list': List
	},
	data() {
		return {
			// Search query
			query: '',
			// Last searched query
			searched: '',
			// Flag if homepage loaded
			loaded: false,
			// Flag if results are loading
			loading: false,
			// Flag if user is typing
			searching: true,
			// Flag if query is updated via params
			paramQuery: false,
			// Current page number
			page: 1,
			// Flag if end of pages reached
			pageEnd: false,
			// Navigation menu with categories
			nav: {
				// Navigation menu items
				items: Categories,
				// Active navigation menu item
				active: Categories[Config.categories[0]],
				// Category used in recent searched query
				searched: -1,
				// Flag if navigation menu is visible (mobile)
				visible: true,
				// Flag if navigation menu is in overflow button (mobile)
				overflow: false
			},
			// Autocompletion functionality
			autocompleter: {
				// Autocomplete items for current query
				items: [],
				// Active autocomplete item
				active: 0,
				// Nonce for autocomplete API requests
				nonce: '',
				// Number of autocomplete items to display
				size: 5,
				// Flag if autocompletion is visible
				visible: false,
				// Flag if user is selecting an autocomplete item
				selecting: false,
				// Timer identifier to show autocomplete results
				timer: ''
			},
			// Results for searched query
			res: [],
			// Smart results for searched query
			sres: [],
			// Suggestions for searched query
			sgres: [],
			// Settings for current user
			settings: {
				// Flag if settings are visible
				visible: false,
				// Settings menu with settings pages
				nav: {
					// Settings menu items
					items: ['General', 'Provider', 'Appearance', 'About'],
					// Active settings menu item
					active: 'General'
				},
				// Information about current Moondrop Search installation
				about: {
					version: '0.1.0',
					license: 'AGPL-3.0',
					source: 'https://github.com/natlec/moondrop-search/',
					credits: [
						{ 
							id: 0, 
							title: 'searx', 
							license: 'AGPL-3.0', 
							desc: 'This is the software which runs on each instance from which all results are retrieved.', url: 'https://github.com/asciimoo/searx/' 
						},
						{ 
							id: 1, 
							title: 'Vue.js', 
							license: 'MIT', 
							desc: 'This is the JavaScript framework which, in conjunction with the Vue CLI, is used to develop the frontend.', 
							url: 'https://vuejs.org/' 
						},
						{ 
							id: 2, 
							title: 'Mada', 
							license: 'OFL-1.1', 
							desc: 'This is the primary font used within the Default, Deep, Mono, Slate Light, Slate Dark, OLED & Fox styles.', 
							url: 'https://github.com/alif-type/mada/' 
						},
						{ 
							id: 3, 
							title: 'Inconsolata', 
							license: 'OFL-1.1', 
							desc: 'This is the primary font used within the Terminal style.', 
							url: 'https://levien.com/type/myfonts/inconsolata.html' 
						},
						{ 
							id: 4, 
							title: 'Nathan Lecompte', 
							license: '© 2019',
							desc: 'This is the dude who made Moondrop Search.', 
							url: 'https://natlec.com' 
						}
					]
				},
				// Instance/Provider settings
				instances: {
					// All available instances for selection
					items: Instances,
					// Instance selected for searching
					active: Instances[Config.instance]
				},
				// Styling settings
				styles: {
					// All available styles for selection
					items: Styles,
					// Style enabled and selected
					active: Styles[Config.style],
					// Flag if a style has been loaded
					loaded: false
				},
				// User configuration for settings
				config: Config
			},
			// Deferred add-to-home-screen event
			pwa: '',
			// Information bubble content
			info: ''
		}
	},
	watch: {
		// Update query when input is updated
		query: function() {
			if(!this.paramQuery) {
				this.searching = true;
				this.loaded = true;
				this.nav.visible = (!this.query && this.loaded && !this.loading);

				// Toggle autocompleter
				this.autocompleter.visible = (this.query && this.loaded && !this.loading);
				if(this.autocompleter.visible && !this.autocompleter.selecting && this.query.length > 2) {
					clearTimeout(this.autocompleter.timer);
					this.autocompleter.timer = setTimeout(() => { this.autoComplete(this.query) }, 50)
				}
			}
		}
	},
	created() {
		// Load user config from localStorage (if available)
		if(localStorage) {
			// Load general config
			this.settings.config = (localStorage.getItem('md_config')) 
				? JSON.parse(localStorage.getItem('md_config')) 
				: Config;
			this.nav.active = Categories[this.settings.config.categories[0]];

			// Load provider config
			this.settings.instances.items = (localStorage.getItem('md_inst')) ? JSON.parse(localStorage.getItem('md_inst')) : Instances;
			this.settings.instances.active = (this.settings.config.instance > -1 && this.settings.config.instance <= this.settings.instances.items.length) 
				? this.settings.instances.items[this.settings.config.instance] 
				: this.settings.instances.items[Config.instance];
			this.settings.instances.newId = this.settings.instances.length;

			// Load style config
			this.settings.styles.active = (this.settings.config.style > -1 && this.settings.config.style <= this.settings.styles.items.length) 
				? this.settings.styles.items[this.settings.config.style] 
				: this.settings.styles.items[Config.style]
		}

		// Load user settings
		this.loadStyle();
		this.loadOverflow();
		
		// Load nonce for autocompleter
		this.autocompleter.nonce = this.getNonce();
		document.cookie = '__Host-ac-nonce=' + this.autocompleter.nonce + ';max-age=3600;path=/;secure=true;samesite=strict';

		// Load Scatter-select if enabled
		if(this.settings.config.scatterSelect && this.settings.instances.items.length > 1) 
			this.loadScatterSelect();

		// Handle global key presses
		window.addEventListener('keydown', this.onKeydown);
		// Handle global resize changes
		window.addEventListener('resize', this.onResize);
		// Handle global scrolling
		window.addEventListener('scroll', this.onScroll);
		// Handle add to home screen function
		window.addEventListener('beforeinstallprompt', (e) => {
			e.preventDefault();
			this.pwa = e
		});
		
		// Check URL parameters
		let url = new URLSearchParams(window.location.search);
		// Check if query & category parameters are set
		if(url.has('q') && url.has('c')) {
			let category = url.get('c');

			// Ensure category parameter exists
			if(!isNaN(category) && !category.match(/-/g)) {
				this.nav.active = (category > -1 && category < Categories.length && this.settings.config.categories.indexOf(parseInt(category, 10)) > -1) 
					? Categories[category] 
					: this.nav.active
			}
		}
		// Check if query parameter is set
		if(url.has('q')) {
			let query = url.get('q');

			this.paramQuery = true;
			this.query = query;
			this.search(query, this.nav.active)
		}
	},
	destroyed() {
		// Remove event handlers when no longer needed
		window.removeEventListener('keydown', this.onKeydown);
		window.removeEventListener('resize', this.onResize);
		window.removeEventListener('scroll', this.onScroll)
	},
	methods: {
		// Flag if current user is on mobile
		isMobile() {
			return window.innerWidth < ((this.nav.items.length * 100) + 800)
		},
		// Handle searching whenever keys are pressed globally (except for mobile)
		onKeydown() {
			if(!this.isMobile() && !this.settings.visible) 
				document.getElementById('q').focus()
		},
		// Handle overflow menu visibility when window is resized
		onResize() {
			this.loadOverflow()
		},
		// Handle sticky navigation menu & autoload on scroll
		onScroll() {
			// Sticky navigation menu
			let header = document.getElementsByTagName('header')[0];
			if(this.settings.config.stickyNav && window.scrollY > 10) {
				header.classList.add('sticky')
			} else {
				header.classList.remove('sticky');
				this.nav.visible = false
			}

			// Autoload results on scroll
			if(this.settings.config.autoload && !this.settings.visible && !this.pageEnd && this.res.length>0 && !this.loading && (window.innerHeight + window.scrollY) > (document.body.offsetHeight-(document.body.offsetHeight/4))) {
				this.loadClick();
				this.loading=true
			}
		},
		// Handle logo click event
		logoClick() {
			if(!this.settings.visible) {
				this.resetSearch()
			} else {
				this.settings.visible=false;
				document.title = (this.settings.config.searchParams && this.query) 
					? this.query+' — Moondrop Search' 
					: 'Moondrop Search';
				this.saveConfig()
			}
			window.scrollTo(0, 0)
		},
		// Handle settings menu item click event
		settingsClick() {
			this.settings.visible = true;
			this.nav.visible = false;
			document.title = 'Settings — Moondrop Search'
		},
		// Handle navigation menu item click event & update current category
		navClick(item) {
			if(item) {
				this.nav.active = item;
				this.nav.visible = (this.loaded && this.searching);
				this.search(this.query, this.nav.active)
			}
		},
		// Function to load the overflow menu according to user configuration
		loadOverflow() {
			this.nav.overflow=(this.isMobile() || this.settings.config.overflowAlways);
			this.nav.visible=!(this.isMobile() || this.settings.config.overflowAlways);

			// Update config changes
			this.saveConfig()
		},
		// Function to toggle a category
		toggleCategory(item) {
			if(item) {
				// Delete category from list if it exists (and vice-versa)
				let index = this.settings.config.categories.indexOf(item.id);
				if(index > -1 && this.settings.config.categories.length > 1) {
					this.settings.config.categories.splice(index, 1)
				} else {
					this.settings.config.categories.push(item.id)
				}

				// Set active category to the first enabled category in list
				this.nav.active = Categories[this.settings.config.categories[0]];
				this.resetSearch()
			}
		},
		// Function to set the current instance
		setInstance(item) {
			if(item) {
				this.settings.instances.active = item;
				this.settings.config.instance = item.id;
				this.resetSearch()
			}
		},
		// Function to delete an instance at index
		deleteInstance(index) {
			if(index >= 0 && index <= this.settings.instances.items.length) {
				this.settings.instances.items.splice(index, 1);
				this.resetSearch()
			}
		},
		// Function to add a new instance
		addInstance(url) {
			// RegEx to validate a URL (accept HTTPS only)
			// eslint-disable-next-line
			let validURL = new RegExp("^((https)?://)?(([0-9a-z_!~*'().&=+$%-]+: )?[0-9a-z_!~*'().&=+$%-]+@)?(([0-9]{1,3}\.){3}[0-9]{1,3}|([0-9a-z_!~*'()-]+\.)*([0-9a-z][0-9a-z-]{0,61})?[0-9a-z]\.[a-z]{2,6}|localhost)(:[0-9]{1,4})?((/?)|(/[0-9a-z_!~*'().;?:@&=+$,%#-]+)+/?)$");

			if(url && validURL.test(url)) {
				let item = { 
					id: this.settings.instances.items.length + 1, 
					content: ((url.lastIndexOf('/') === url.length-1) 
						? url 
						: url+'/')
				};

				// Check if URL already exists
				let duplicate = false;
				for(let i = 0; i < this.settings.instances.items.length; i++) {
					if(this.settings.instances.items[i].content === url) {
						duplicate = true;
						break
					}
				}

				// If no duplicate is found: add it to the list
				if(!duplicate) {
					this.settings.instances.items.push(item);
					this.resetSearch()
				}
			}
		},
		// Function to set the current style
		setStyle(item) {
			if(item) {
				this.clearStyle();
				this.settings.styles.active = item;
				this.settings.config.style = item.id;
				this.loadStyle()
			}

			// Update config changes
			this.saveConfig()
		},
		// Function to clear any loaded style
		clearStyle() {
			if((this.settings.styles.active.fonts || this.settings.styles.active.css) && this.settings.styles.loaded) {
				let style = window.document.styleSheets[0];

				// Get number of CSS rules for current style
				let rules = ((this.settings.styles.active.fonts && !this.settings.config.liteMode) && this.settings.styles.active.css) 
					? this.settings.styles.active.fonts.length + 1 
					: (this.settings.styles.active.fonts && !this.settings.config.liteMode) 
						? this.settings.styles.active.fonts.length 
						: (this.settings.styles.active.css) 
							? 1 
							: 0;

				// Delete rules for current style
				for(let i = 0; i < rules; i++) {
					style.deleteRule(style.cssRules.length - 1)
				}

				// No style is loaded
				this.settings.styles.loaded = false
			}

			// Update config changes
			this.saveConfig()
		},
		// Function to load style according to user configuration
		loadStyle() {
			if(!this.settings.styles.loaded) {
				let style = window.document.styleSheets[0];

				// Add the CSS for the current style
				if(this.settings.styles.active.css) {
					// Add main CSS for current style, enable/disable animations according to Lite mode & set primary font
					style.insertRule(':root{' + 
						this.settings.styles.active.css.replace(/[\])}[{(]/g,'') + 
							((this.settings.config.liteMode) 
								? ';--animationSpeed:0;--primaryFont:-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif'
								: ';--animationSpeed:.2s;--primaryFont:' + this.settings.styles.active.fonts[0].family.replace(/[\])}[{(]/g,'') + ',system-ui,sans-serif')
							+ '}', style.cssRules.length);
				
					// Add main CSS for fonts of current style (if Lite mode is disabled)
					if(this.settings.styles.active.fonts && !this.settings.config.liteMode) {
						this.settings.styles.active.fonts.forEach(font => {
							let fontsrc = '';

							for(let i = 0; i < font.src.length; i++) {
								fontsrc += ",url('" + font.src[i].url.replace(/[\])}[{(]/g,'') + "')" + 
									" format('" + font.src[i].format.replace(/[\])}[{(]/g,'') + "')"
							}

							style.insertRule("@font-face{" + 
								"font-family:" + font.family.replace(/[\])}[{(]/g,'') + 
								";font-weight:" + font.weight.toString().replace(/\D+/g,'') + 
								";font-style:normal;font-display:swap;unicode-range:U+000-5FF;src:local('" + font.name.replace(/[\])}[{(]/g,'') + "')" + fontsrc + "}", style.cssRules.length)
						})
					}

					// Style is loaded
					this.settings.styles.loaded = true
				}
			}

			// Update config changes
			this.saveConfig()
		},
		// Handle load more results button click event
		loadClick() {
			if(!this.pageEnd) {
				this.page++;
				this.getResults(this.query, this.nav.active.content, this.page)
			}
		},
		// Function to prompt the user to add Moondrop Search to their Home screen
		addPWAClick() {
			if(this.pwa) {
				this.pwa.prompt();
				this.pwa.userChoice.then((result) => {
					if (result.outcome === 'accepted')
						this.info = 'Successfully added to Home screen!';
					this.pwa = null
				})
			} else {
				this.info = 'Error: Unable to add to Home screen'
			}
		},
		// Function to toggle Lite mode
		toggleLiteMode() {
			if(this.settings.styles.loaded) {
				this.setStyle(this.settings.styles.active)
			}
		},
		// Function to select a random instance
		loadScatterSelect() {
			let item = this.settings.instances.items[~~(Math.abs(Math.sin(window.crypto.getRandomValues(new Uint32Array(1))[0])) * this.settings.instances.items.length)];
			this.settings.instances.active = item;
			this.settings.config.instance = item.id;
			this.saveConfig()
		},
		// Function to save user configuration into localStorage (if available)
		saveConfig() {
			if(localStorage) {
				localStorage.setItem('md_config', JSON.stringify(this.settings.config));
				localStorage.setItem('md_inst', JSON.stringify(this.settings.instances.items))
			}
		},
		// Function to reset search
		resetSearch() {
			this.loaded = false;
			this.searching = true;
			this.nav.visible = false;
			this.query = '';
			this.searched = '';

			// Reset document title
			document.title = (!this.settings.visible) 
				? 'Moondrop Search' 
				: document.title;

			// Remove search params
			let url = new URLSearchParams(window.location.search);
			url.delete('q');
			url.delete('c');
			history.pushState(null, '', window.location.pathname);

			// Update config changes
			this.saveConfig()
		},
		// Function to get a new nonce
		getNonce() {
			const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz';
			let result=[], nonce='';

			// Generate a cryptographically-secure random nonce
			window.crypto.getRandomValues(new Uint8Array(40)).forEach(c =>
				result.push(chars[c % chars.length]));
			nonce = result.join('');

			return nonce
		},
		// Function to move up to next active autocomplete item
		autoCompleteUp() {
			if(this.autocompleter.visible && this.autocompleter.items.length > 1) {
				this.autocompleter.active = (this.autocompleter.active-1 < 0) 
					? this.autocompleter.items.length-1 
					: this.autocompleter.active-1;
				
				// Change query without sending new API request
				this.autocompleter.selecting = true;
				this.query = this.autocompleter.items[this.autocompleter.active].val
			}
		},
		// Function to move down to next active autocomplete item
		autoCompleteDown() {
			if(this.autocompleter.visible && this.autocompleter.items.length > 1) {
				this.autocompleter.active = (this.autocompleter.active+2>this.autocompleter.items.length) 
					? 0 
					: this.autocompleter.active+1;

				// Change query without sending new API request
				this.autocompleter.selecting = true;
				this.query = this.autocompleter.items[this.autocompleter.active].val
			}
		},
		// Function to autocomplete passed query
		autoComplete(query) {
			if(query && this.searching) {
				// Clear previous results
				this.autocompleter.items = [];

				// Load autocomplete results
				this.getAutoComplete(query)
			}
		},
		// Function to retrieve autocomplete results using API
		getAutoComplete(query) {
			if(query) {
				let md = this;
				md.autocompleter.active = 0;

				// Use Fetch to send GET request to API
				fetch('api/ac/?i=' + md.settings.instances.active.content + '&q=' + encodeURIComponent(query) + '&nonce=' + md.autocompleter.nonce, {
					method: 'GET',
					headers: new Headers({
						'User-Agent': 'Moondrop Search'
					})
				}).then(response => {
					// Parse JSON response
					return response.json();
				}).then(json => {
					if(!json.error) {
						// Load autocomplete items
						if(json.length > 1) {
							let id = 0;
							json.forEach(val => {
								let item = { id: id, val: val };
								md.autocompleter.items.push(item);
								id++
							});

							// Set size accordingly
							md.autocompleter.items = md.autocompleter.items.slice(0, md.autocompleter.size);
						}

						// Allow new API requests
						clearTimeout(md.autocompleter.timer)
					} else {
						// eslint-disable-next-line
						console.log(json.error)
					}
				}).catch(error => {
					// eslint-disable-next-line
					console.log(error)
				})
			}
		},
		// Function to search for passed query
		search(query, category) {
			if((query && (query != this.searched || this.searching)) || (query && (this.nav.active.id != this.nav.searched || this.searching))) {
				// Un-focus searchbox (hide keyboard on mobile)
				document.activeElement.blur();

				// Clear previous results
				this.sgres = [];
				this.sres = [];
				this.res = [];

				// Update variables
				this.loaded = true;
				this.searching = false;
				this.nav.visible = false;
				this.autocompleter.visible = false;
				this.nav.searched = this.nav.active.id;
				this.query = query;
				this.searched = query;
				this.page = 1;
				this.pageEnd = false;

				// Set search parameters if enabled
				if(this.settings.config.searchParams) {
					document.title = query+' — Moondrop Search';

					let url = new URLSearchParams(window.location.search);
					url.set('q', query);
					url.set('c', category.id);
					let newUrl = window.location.pathname + '?' + url.toString();
					history.pushState(null, '', newUrl)
				}

				// Select a random instance if Scatter-select is enabled
				if(this.settings.config.scatterSelect && this.settings.instances.items.length > 1) 
					this.loadScatterSelect();
				
				// Load results for current search
				this.getResults(query, category.content, 1)
			}
		},
		// Function to retrieve search results using API
		getResults(query, category, page) {
			if(query && category && page) {
				let md = this;
				md.loading = true;
				
				// Set a new nonce for every API request
				const nonce = md.getNonce();
				document.cookie = '__Host-res-nonce=' + nonce + ';max-age=3600;path=/;secure=true;samesite=strict';

				// Use Fetch to send POST request to API
				fetch('api/res/', {
					method: 'POST',
					headers: new Headers({
						'Content-Type': 'application/x-www-form-urlencoded',
						'User-Agent': 'Moondrop Search'
					}),
					body: 'i=' + md.settings.instances.active.content + '&q=' + encodeURIComponent(query) + '&c=' + category + '&p=' + page + '&ss=' + md.settings.config.safeSearch + '&nonce=' + nonce
				}).then(response => {
					// Parse JSON response
					return response.json();
				}).then(json => {
					if(!json.error) {
						// Load suggestions
						if(page === 1 && md.settings.config.suggestions && json.suggestions.length > 0 && json.results.length > 0) {
							for(let i = 0; i < json.suggestions.length; i++) {
								let sgresItem = { id: i, title: json.suggestions[i] };
								md.sgres.push(sgresItem)
							}
						}

						// Load smart results
						if(page === 1 && md.settings.config.smartResults && json.infoboxes.length > 0 && json.results.length > 0) {
							let sid = 0;
							json.infoboxes.forEach(item => {
								let sresItem = { 
									id: sid, 
									resType: md.nav.active.name, 
									img: item.img_src, 
									title: item.infobox, 
									desc: item.content, 
									snippets: [], 
									links: [] 
								};

								// Load attributes for smart result
								if(item.attributes) {
									let aid = 0;
									item.attributes.forEach(att => {
										let snippet = {
											id: aid, 
											title: att.label, 
											content: att.value 
										};
										sresItem.snippets.push(snippet);
										aid++
									})
								}

								// Load links for smart result
								if(item.urls) {
									let uid = 0;
									item.urls.forEach(url => {
										let link = { 
											id: uid, 
											title: url.title, 
											url: url.url 
										};
										sresItem.links.push(link);
										uid++
									})
								}

								md.sres.push(sresItem);
								sid++
							});
						}

						// Load results
						if(json.results.length>0) {
							let rid = 0;
							json.results.forEach(item => {
								switch(md.nav.active.id) {
									// General search results
									case 0:
										if(item.url && item.parsed_url && item.title && item.content && item.engine) {
											let resItem = { 
												id: rid, 
												url: item.url, 
												icon: 'https://icons.duckduckgo.com/ip3/' + item.parsed_url[1] + '.ico', 
												title: item.title, 
												desc: item.content,
												engine: item.engine
											};
											md.res.push(resItem)
										}
										break;

									// Image search results
									case 1:
										if(item.url && item.title && item.thumbnail_src && item.img_src && item.engine) {
											let iresItem = { 
												id: rid, 
												url: item.url, 
												title: item.title, 
												img: { 
													srcThumb: (item.thumbnail_src.indexOf('data:') < 0) 
														? item.thumbnail_src 
														: '', 
													src: (item.img_src.indexOf('data:') < 0) 
														? item.img_src
														: ''
												}, 
												engine: item.engine 
											};
											md.res.push(iresItem)
										}
										break;

									// Video search results
									case 2:
										if(item.url && item.parsed_url && item.title && item.thumbnail && item.engine) {
											let vresItem = { 
												id: rid, 
												url: item.url, 
												icon: 'https://icons.duckduckgo.com/ip3/' + item.parsed_url[1] + '.ico', 
												title: item.title, 
												img: (item.thumbnail.indexOf('data:') < 0) 
													? item.thumbnail
													: '', 
												engine: item.engine 
											};
											md.res.push(vresItem)
										}
										break;

									// News search results
									case 3:
										if(item.url && item.parsed_url && item.title && item.content && item.img_src && item.engine) {
											let nresItem = { 
												id: rid, 
												url: item.url, 
												icon: 'https://icons.duckduckgo.com/ip3/' + item.parsed_url[1] + '.ico', 
												title: item.title, 
												desc: item.content, 
												img: (item.img_src.indexOf('data:') < 0) 
													? item.img_src
													: '', 
												engine: item.engine 
											};
											md.res.push(nresItem)
										}
										break;

									default:
										md.info = 'Error: Selected search category is invalid.'
								}

								rid++
							});
							md.loading = false
						} else {
							md.loading = false;
							md.pageEnd = true
						}
					} else {
						md.loading = false;
						md.pageEnd = true;
						md.info = 'Error: ' + json.error;

						// eslint-disable-next-line
						console.log(json.error)
					}
				}).catch(error => {
					md.loading = false;
					md.pageEnd = true;
					md.info = 'Error: ' + error.toString();

					// eslint-disable-next-line
					console.log(error)
				});
			}
		}
	}
}
</script>


<style>
::selection {
	background-color: rgb(var(--accentColor));
	color: rgb(var(--backgroundColor))
}
::-moz-selection {
	background-color: rgb(var(--accentColor));
	color: rgb(var(--backgroundColor))
}
::placeholder {
	color: rgb(var(--primaryColor));
	opacity: var(--primaryOpacity)
}
body {
	margin: 0 15%;
	background: rgb(var(--backgroundColor));
	color: rgba(var(--primaryColor), var(--primaryOpacity));
	font: 400 1em var(--primaryFont);
	line-height: 1.5;
	scroll-behavior: smooth;
	text-rendering: auto;
	-webkit-font-smoothing: auto;
	-moz-osx-font-smoothing: auto;
	overflow-y: scroll;
	-webkit-user-drag: none;
	user-select: none;
	-moz-user-select: none;
	-webkit-user-select: none;
	-ms-user-select: none
}
svg {
	fill: rgb(var(--accentColor))
}
noscript {
	position: fixed;
	text-align: center;
	top: 10vh;
	left: 50px;
	right: 50px
}
hr {
	height: 1px;
	border: none;
	background: rgba(var(--primaryColor),.05);
	margin: 20px 0
}


/* Header */
header {
	display: flex;
	position: fixed;
	left: 0;
	top: 0;
	width: 60%;
	height: 100%;
	padding: 0 20%;
	align-items: center;
	background: linear-gradient(rgb(var(--backgroundColor)),transparent);
	transition: var(--animationSpeed) ease;
	z-index: 999
}
.static {
	position: absolute
}
.md header {
	width: 70%;
	height: 80px;
	padding: 0 15%;
	box-shadow: 0 0 0 1px rgba(var(--primaryColor),.04)
}
.md .sticky {
	position: fixed;
	height: 60px;
	background: linear-gradient(rgb(var(--backgroundColor)),rgba(var(--backgroundColor),.98));
	box-shadow: 0 0 0 1px rgba(var(--primaryColor),.04), 
		0 2px 16px rgba(var(--primaryColor),.04)
}


/* Header > Searchbox */
.search {
	position: relative;
	display: inline-flex;
	flex-grow: 1;
	align-items: center
}
.md .search {
	height: inherit
}
.search .logo, 
.search .logo-back {
	border-radius: 30px;
	min-width: 24px;
	vertical-align: middle;
	outline: none;
	-webkit-tap-highlight-color: transparent;
	transition: var(--animationSpeed) ease;
	flex-shrink: 0
}
.search .logo {
	width: 50px;
	height: 50px;
	margin-left: -50px;
	opacity: 1;
	transform: none;
	background: rgb(var(--backgroundColor))
}
.search .hide-logo {
	opacity: 0;
	z-index: -1;
	transform: scale(0)
}
.search .logo-back {
	width: 24px;
	height: 24px;
	padding: 13px;
	opacity: 1;
	cursor: pointer;
	transform: none
}
.search .hide-logo-back {
	opacity: 0;
	transform: translateX(-50px) scale(0)
}
.search .logo-back:active,
.search .logo-back:focus {
	background: rgba(var(--accentColor), .05)
}
.search span {
	display: flex;
	flex: auto
}
.search #q, 
.search button {
	height: 50px;
	font: 400 .95em var(--primaryFont);
	flex: auto;
	outline: none
}
.search #q {
	flex-grow: 1;
	padding: 0 20px;
	background: transparent;
	border: none;
	color: rgb(var(--primaryColor));
	transition: var(--animationSpeed) ease
}
.search #q:focus::placeholder {
	opacity: .5
}
.md .search #q {
	padding: 0 10px
}
.search #q::-ms-clear {
	display: none
}
.search label {
	position: absolute;
	clip: rect(0px, 0px, 0px, 0px)
}
.search button {
	display: inline-block;
	flex-grow: 0;
	min-width: 50px;
	background: transparent;
	border: none;
	border-radius: 2px;
	cursor: pointer;
	-webkit-tap-highlight-color: transparent;
	transition: var(--animationSpeed) ease
}
.search button > svg {
	min-width: 16px;
	margin: 4px 1px 0 0
}
.md .search button > svg {
	padding: 0
}
.md .search button svg:active {
	transform: none;
	border-radius: 0;
	background: none
}
.search button:active, 
.overflow:active,
.search button:focus, 
.overflow:focus {
	background: rgba(var(--accentColor), .05)
}

/* Header > Overflow menu button */
.overflow span {
	display: block;
	flex: none
}
.overflow svg {
	margin: 6px 1px 0 0 !important
}

/* Header > Categories menu */
nav {
	display: flex;
	align-items: center
}
nav span {
	display: flex;
	width: 100%;
	align-items: center;
	flex-flow: column nowrap
}
nav a, 
.nav-settings {
	display: inline;
	position: relative;
	padding: 14px 20px;
	margin-left: -1px;
	border-radius: 2px;
	font-size: .9em;
	font-weight: 400;
	color: rgba(var(--primaryColor), var(--primaryOpacity));
	text-decoration: none;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	max-width: 100px;
	min-width: 20px;
	cursor: pointer;
	outline: none;
	-webkit-tap-highlight-color: transparent;
	transition: var(--animationSpeed) ease
}
nav a:hover {
	color: rgb(var(--primaryColor))
}
nav a:active, 
nav a:focus,
nav .active, 
nav .active:hover, 
nav .active:active {
	font-weight: 600;
	color: rgb(var(--accentColor))
}
nav a:active, 
nav a:focus,
.nav-settings:active {
	background: rgba(var(--accentColor), .05)
}
nav .active:active,
nav .active:focus {
	background: transparent
}

/* Header > Categories menu (overflow) */
.o-nav {
	display: flex;
	position: fixed;
	background: rgba(var(--backgroundColor),.98);
	top: 0;
	left: 0;
	width: calc(100% - 40px);
	height: calc(100% - 40px);
	padding: 20px;
	align-items: center;
	flex-flow: column;
	overflow-x: hidden;
	transition: calc(var(--animationSpeed)/2) ease;
	z-index: 999
}
.md .o-nav {
	top: 70px;
	height: calc(100% - 100px)
}
.md .sticky .o-nav {
	top: 60px
}
.o-nav a {
	display: block;
	min-width: 60%;
	text-align: center;
	font-size: 1em;
	padding: 5vh 0
}
.o-nav a:active {
	transform: scale(.98)
}
.o-nav .o-close:hover {
	background: none
}
.o-nav .o-close:active {
	background: rgba(var(--accentColor), .05)
}
.o-nav .o-close svg {
	margin: 3px 1px 0 0
}

/* Header > Autocomplete menu */
.ac {
	display: block;
	position: absolute;
	font: 400 .95em var(--primaryFont);
	margin: 0;
	padding: 0;
	top: 100px;
	left: 50px;
	right: 0;
	list-style: none
}
.ac li {
	padding: 10px;
	border-radius: 2px;
	cursor: pointer;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	-webkit-tap-highlight-color: transparent;
	transition: var(--animationSpeed) ease
}
.ac li:hover {
	color: rgb(var(--primaryColor))
}
.ac li:active {
	background: rgba(var(--accentColor), .05);
}
.ac .active, 
.ac .active:hover {
	color: rgb(var(--accentColor));
	font-weight: 600
}


/* Information bubble */
.info {
	position: fixed;
	display: flex;
	align-items: center;
	left: 50%;
	bottom: 30px;
	padding: 5px;
	transform: translateX(-50%);
	background: rgba(var(--backgroundColor), .98);
	-webkit-tap-highlight-color: transparent;
	border-radius: 2px;
	box-shadow: 0 0 0 1px rgba(var(--primaryColor), .05);
	font-size: .95em;
	text-align: center;
	cursor: pointer;
	z-index: 999;
	transition: var(--animationSpeed) ease
}
.md .info {
	box-shadow: 0 0 0 1px rgba(var(--primaryColor), .05), 
		0 2px 4px rgba(var(--primaryColor), .05);
}
.info:active {
	transform: translateX(-50%) scale(.99)
}
.info span, 
.info button {
	padding: 12px 15px
}
.info button {
	background: transparent;
	border: none;
	border-radius: 2px;
	outline: none;
	-webkit-tap-highlight-color: transparent;
	cursor: pointer;
	transition: var(--animationSpeed) ease
}
.info button svg {
	margin: 3px 1px 0 0
}
.info button:active,
.info button:focus {
	background: rgba(var(--accentColor), .05)
}
.info-enter-active, 
.info-leave-active {
	transition: calc(var(--animationSpeed)/1.25) ease
}
.info-enter, 
.info-leave-to  {
	opacity: 0;
	bottom: -10px;
	transform: translateX(-50%) scale(.99)
}


/* Default transitions */
.woosh-enter-active, 
.woosh-leave-active {
	transition: calc(var(--animationSpeed)/1.25) ease
}
.woosh-enter {
	opacity: 0;
	transform: translateY(-5px)
}
.woosh-leave-to {
	opacity: 0;
	transform: translateY(10px) scale(.98)
}


/* Search suggestions */
.res-suggestions {
	display: block;
	padding: 0 10px 20px;
	margin-bottom: 20px;
	text-align: left;
	border-bottom: 1px solid rgba(var(--primaryColor), .05)
}
.res-suggestions span {
	display: inline-block
}
.res-suggestions a {
	display: block;
	padding: 14px;
	text-decoration: none;
	font-weight: 600;
	font-size: .95em;
	color: rgb(var(--accentColor));
	outline: none;
	-webkit-tap-highlight-color: transparent;
	transition: var(--animationSpeed) ease
}
.res-suggestions a:active,
.res-suggestions a:focus {
	background: rgba(var(--accentColor), .05);
	border-radius: 2px
}

/* Loading indicators */
.loading {
	display: block;
	position: fixed;
	top: 50%;
    left: 50%;
	width: 60px;
	height: 60px;
	border-radius: 60px;
    transform: translate(-50%, -50%);
	background: rgba(var(--primaryColor), .1) 
		linear-gradient(transparent, rgba(var(--primaryColor), .1), transparent);
	background-repeat: repeat no-repeat;
	background-size: 60px;
	animation: loading calc(var(--animationSpeed)*4) linear infinite;
	z-index: -1
}
.loading svg {
	width: 60px;
	height: 60px;
	fill: rgba(var(--backgroundColor), .5)
}
@keyframes loading {
    0% {
		background-position: 0 -60px
	}
	100% {
		background-position: 0 60px
	}
}

.loading-small {
	display: inline-block;
	width: 40px;
	height: 40px;
	border-radius: 40px;
	background: rgba(var(--primaryColor), .1) 
		linear-gradient(transparent, rgba(var(--primaryColor), .1), transparent);
	background-repeat: repeat no-repeat;
	background-size: 40px;
	animation: loading-small calc(var(--animationSpeed)*4) linear infinite;
	z-index: -1
}
.loading-small svg {
	width: 40px;
	height: 40px;
	fill: rgba(var(--backgroundColor), .5)
}
@keyframes loading-small {
    0% {
		background-position: 0 -40px
	}
	100% {
		background-position: 0 40px
	}
}

/* Search results */
.results {
	display: flex;
	margin-top: 100px
}
.res {
	order: 0;
	max-width: 50%
}
.res-item {
	display: block;
	padding: 20px;
	border-radius: 2px;
	color: rgb(var(--accentColor));
	text-decoration: none;
	outline: none;
	-webkit-tap-highlight-color: transparent;
	transition: calc(var(--animationSpeed)/2) ease
}
.res-item:visited {
	color: var(--linkVisitedColor)
}
.res-item:hover,
.res-item:focus {
	color: rgb(var(--primaryColor));
	background: rgba(var(--primaryColor), .025)
}
.res-item:active {
	transform: scale(.98);
	background: rgba(var(--primaryColor), .05)
}
.res-item h2 {
	margin: 0;
	font-size: 1.05em;
	font-weight: 600;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis
}
.res-item p {
	margin: 2px 0 4px;
	font-size: .95em;
	color: rgba(var(--primaryColor), var(--primaryOpacity));
}
.res-item span {
	display: block;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	font-size: .9em;
	color: rgb(var(--primaryColor))
}
.res-item span img {
	margin: -2px 5px 0 0;
	width: 16px;
	min-width: 16px;
	height: 16px;
	border-radius: 0;
	vertical-align: middle
}

/* Smart search results */
.s-res {
	order: 1;
	max-width: 45%;
	margin-left: 5%
}
.s-res-item {
	display: block;
	padding: 20px;
	margin-bottom: 20px;
	border-bottom: 1px solid rgba(var(--primaryColor), .05);
	font-size: .95em;
	-webkit-user-drag: auto;
	user-select: text;
	-moz-user-select: text;
	-webkit-user-select: text;
	-ms-user-select: text
}
.s-res-item h2 {
	font: 600 1.4em var(--primaryFont);
	color: rgb(var(--primaryColor))
}
.s-res-item p ul {
	list-style: none;
	padding: 0
}
.s-res-item p li {
	padding: 15px 0;
	border-top: 1px solid rgba(var(--primaryColor), .05)
}
.s-res-item p li b {
	margin-right: 10px;
	font-weight: 600;
	color: rgb(var(--primaryColor))
}
.s-res-links {
	margin: 20px -15px 0;
	list-style: none;
	color: rgba(var(--primaryColor), .25);
	padding: 0;
	-webkit-user-drag: none;
	user-select: none;
	-moz-user-select: none;
	-webkit-user-select: none;
	-ms-user-select: none
}
.s-res-links li {
	display: inline-block
}
.s-res-links li a {
	display: inline-block;
	margin: 0 5px;
	padding: 14px;
	text-decoration: none;
	font-weight: 600;
	font-size: .95em;
	color: rgb(var(--accentColor));
	outline: none;
	-webkit-tap-highlight-color: transparent;
	transition: var(--animationSpeed) ease
}
.s-res-links li a:active,
.s-res-links li a:focus {
	background: rgba(var(--accentColor), .05);
	border-radius: 2px
}
.s-res-image {
	display: flex;
	margin: -16px 0 40px;
	overflow: hidden;
	box-shadow: 0 1px 2px rgba(var(--primaryColor), .025), 
		inset 0 0 0 1px rgba(var(--primaryColor), .05);
	border-radius: 2px
}
.s-res-image img {
	width: 100%;
	height: 100%;
	max-height: 20vh;
	object-fit: cover
}

/* Results > End of results */
.res-load {
	display: block;
	margin: 20px auto;
	width: 100%;
	max-width: 50vw;
	padding: 20px;
	border: none;
	border-radius: 2px;
	background: none;
	text-decoration: none;
	text-align: center;
	cursor: pointer;
	outline: none;
	-webkit-tap-highlight-color: transparent;
	transition: calc(var(--animationSpeed)/2) ease
}
.res-load:hover,
.res-load:focus {
	background: rgba(var(--accentColor), .025)
}
.res-load:active {
	transform: scale(.98);
	background: rgba(var(--accentColor), .05)
}
.res-load svg {
	display: inline-block
}

.res-end {
	display: block;
	width: 100%;
	padding: 25px 0;
	font: 600 .95em var(--primaryFont);
	color: rgb(var(--primaryColor));
	text-align: center
}


/* Settings */
.h1-settings {
	position: absolute;
	font-size: 1.05em;
	font-weight: 600;
	color: rgb(var(--accentColor));
	margin: -42px 0 0 70px;
	transition: var(--animationSpeed) ease
}
.md .h1-settings {
	margin: -37px 0 0 60px
}
.nav-settings {
	padding: 14px 15px
}
.nav-settings svg {
	margin: 0 0 -6px 0
}
.settings {
	display: flex;
	max-width: 50%;
	margin: 100px 20px 40px 20px;
	flex-direction: column
}
.settings h2 {
	font-size: 1em;
	font-weight: 600;
	color: rgb(var(--primaryColor))
}
.settings section {
	display: block;
	margin-bottom: 20px
}
.settings p {
	display: inline-block;
	margin: 0 0 20px !important
}
.settings p .toggle {
	display: block;
	width: 66px;
	margin: 10px -10px -20px
}
.settings button svg {
	margin: 1px 0 -1px 0
}

/* Settings > Button component */
.button {
	position: relative;
	display: inline-block;
	margin: 0 5px;
	padding: 14px 20px;
	background: none;
	box-shadow: 0 1px 2px rgba(var(--primaryColor), .025);
	border: 1.2px solid rgb(var(--primaryColor), .075);
	border-radius: 2px;
	text-decoration: none;
	cursor: pointer;
	font: 600 .95em var(--primaryFont);
	color: rgb(var(--accentColor));
	outline: none;
	-webkit-tap-highlight-color: transparent;
	transition: var(--animationSpeed) ease
}
.button:active,
.button:focus {
	background: rgba(var(--accentColor), .05);
	box-shadow: none;
	border: 1.2px solid rgb(var(--primaryColor), .05)
}
.settings .button {
	display: block;
	margin: 15px 0 5px
}

/* Settings > About */
.settings-about h1 {
	color: rgb(var(--primaryColor));
	margin-bottom: 40px
}
.settings-about svg {
	vertical-align: middle;
	margin: -5px 15px 0 0
}
.settings-about ul {
	list-style: none;
	padding: 15px;
	margin: 0;
	font-size: .95em
}
.settings-about li {
	padding: 15px 0;
	border-top: 1px solid rgba(var(--primaryColor), .05)
}
.settings-about b {
	margin-right: 15px;
	color: rgb(var(--primaryColor))
}
.settings-about a {
	border-radius: 2px;
	padding: 5px;
	margin: -5px;
	text-decoration: none;
	font-weight: 600;
	color: rgb(var(--accentColor));
	outline: none;
	-webkit-tap-highlight-color: transparent
}
.settings-about a:focus {
	background: rgba(var(--accentColor), .05);
	border-radius: 2px
}


/* Media queries (mobile/responsive) */
@media (max-width: 1500px) {
	body {
		margin: 0 10%
	}
	.md header {
		width: 80%;
		padding: 0 10%
	}
}
@media (max-width: 1400px) {
	body {
		margin: 0 5%
	}
	.md header {
		width: 90%;
		padding: 0 5%
	}
}
@media (max-width: 1100px) {
	header {
		width: 80%;
		padding: 0 10%
	}
	.results {
		display: block
	}
	.res, 
	.s-res {
		max-width: 100%;
		margin: 0
	}
	.settings {
		max-width: 100%
	}
}
@media (max-width: 600px) {
	body {
		margin: 0
	}
	header {
		width: calc(100% - 40px);
		padding: 0 20px
	}
	.md header {
		width: calc(100% - 20px);
		padding: 0 10px
	}
	.o-nav {
		width: 100%;
		padding: 20px 0
	}
	.o-nav a {
		width: 100%;
		max-width: 100%
	}
	.ac li {
		padding: calc(1vh + 10px) 20px;
		margin: 0 -10px
	}
	.res-load {
		max-width: 100%
	}
	.info {
		max-width: calc(100% - 30px);
		left: 10px;
		right: 10px;
		bottom: 10px;
		margin: 0 auto;
		transform: none
	}
	.info:active {
		-webkit-transform: scale(.98);
		transform: scale(.98)
	}
}
@media (max-width: 400px) {
	.search {
		max-width: 100%
	}
	.search > span {
		max-width: calc(100% - 50px)
	}
	.search #q { 
		padding: 0 0 0 20px;
		max-width: calc(100% - 120px)
	}
	.md .search #q {
		max-width: calc(100% - 125px)
	}
	.overflow {
		margin: 0
	}
	.res-suggestions {
		margin: 0 0 20px
	}
}
</style>
